import React, { useEffect } from 'react';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { useCommonEligibilityConditionsForJtbdOnboarding } from '@atlassian/jira-onboarding-core/src/controllers/use-fetch-onboarding-personalisation/index.tsx';
import { useAnalyticsEvents, fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useContainerContext } from '@atlassian/jira-providers-container-context/src/main.tsx';
import { ProjectContextProvider } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { useRouter } from '@atlassian/react-resource-router';
import {
	useQuickstartInitialise,
	useQuickstartInitialiseWithPersonalisation,
} from '../../controllers/quickstart-initialise/index.tsx';
import type { RenderOnboardingQuickStartProps } from './types.tsx';

/*
    This hook will be used in spa-app in order to set the shouldRenderComponent
    when it is loaded in side of the proper paths. Once it is unmounted it should set the component to false.
    Next-Gen:
        Board: jira/software/projects/MT1/boards/1 --> spa-app/next-gen-board
        Backlog: jira/software/projects/MT1/boards/1/backlog --> spa-app/next-gen-backlog
        Roadmap: jira/software/projects/MT1/boards/1/roadmap --> spa-app/roadmap/src/view/common/roadmap
        Project Setting (Details): /jira/software/projects/MT1/settings/details --> spa-app/project-settings/details
        Pages: projects/MT1?selectedItem=com.atlassian.jira.jira-projects-plugin:project-pages-page --> spa-app/project/pages
    Classic:
        Board: secure/RapidBoard.jspa?projectKey=CK&rapidView=5 --> spa-app/rapid-board
        Backlog: secure/RapidBoard.jspa?rapidView=5&projectKey=CK&view=planning.nodetail&issueLimit=100 --> spa-app/rapid-board
    Both:
        OpsGenie: projects/CK?selectedItem=com.atlassian.jira.jira-projects-plugin:opsgenie-schedule-page --> spa-app/project/opsgenie-schedule

*/

export const QuickstartShouldRenderWithoutPersonalisation = (
	props: RenderOnboardingQuickStartProps,
) => {
	const [{ match, route }] = useRouter();
	const { projectContext, containerType = 'project' } = props;

	// Require to calculate analytics attributes in this component
	const [{ data: containerContext }] = useContainerContext();
	const projectContextData = projectContext && projectContext.data;
	const { canShowExperience, isEnrolledForProjectSettings } = useQuickstartInitialise(
		projectContextData,
		containerType,
	);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (canShowExperience && isEnrolledForProjectSettings) {
			const analyticsEvent = createAnalyticsEvent({});

			fireTrackAnalytics(analyticsEvent, 'invitePeopleToProject shouldRender', {
				isNextGen: Boolean(projectContextData && projectContextData.simplified),
			});
		}
	}, [
		canShowExperience,
		containerContext,
		createAnalyticsEvent,
		isEnrolledForProjectSettings,
		match,
		projectContextData,
		route,
	]);

	return null;
};

const QuickstartShouldRenderWithPersonalisation = (props: RenderOnboardingQuickStartProps) => {
	const [{ match, route }] = useRouter();
	const { projectContext } = props;

	// Require to calculate analytics attributes in this component
	const [{ data: containerContext }] = useContainerContext();
	const projectContextData = projectContext && projectContext.data;
	const { canShowExperience, isEnrolledForProjectSettings } =
		useQuickstartInitialiseWithPersonalisation(projectContextData);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (canShowExperience && isEnrolledForProjectSettings) {
			const analyticsEvent = createAnalyticsEvent({});

			fireTrackAnalytics(analyticsEvent, 'invitePeopleToProject shouldRender', {
				isNextGen: Boolean(projectContextData && projectContextData.simplified),
			});
		}
	}, [
		canShowExperience,
		containerContext,
		createAnalyticsEvent,
		isEnrolledForProjectSettings,
		match,
		projectContextData,
		route,
	]);

	return null;
};

export const QuickstartShouldRender = componentWithCondition(
	() => {
		return useCommonEligibilityConditionsForJtbdOnboarding();
	},
	QuickstartShouldRenderWithPersonalisation,
	QuickstartShouldRenderWithoutPersonalisation,
);

const QuickstartShouldRenderWithProjectContext = (): React.JSX.Element => (
	<JSErrorBoundary
		id="QuickStartShouldRender"
		packageName="jiraSoftwareOnboardingQuickstart"
		sendToPrivacyUnsafeSplunk
		teamName="jlove-makkuro"
		fallback="unmount"
	>
		<ProjectContextProvider>
			{(projectContext) => <QuickstartShouldRender projectContext={projectContext} />}
		</ProjectContextProvider>
	</JSErrorBoundary>
);

export default QuickstartShouldRenderWithProjectContext;
